/** Something on the query image tagged with the product(s) it shows. */
export interface ProductSpot {
  /** Normalized 0-1 polygon. */
  points: number[][];
  productIds: string[];
}

/** Center [x, y] of a normalized points polygon. */
export const pointsCenter = (pts: number[][]): [number, number] => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return [(minX + maxX) / 2, (minY + maxY) / 2];
};

/**
 * Product ids of the `count` spots whose centres are nearest `points`'
 * centre, closest first, deduplicated. Spots without points or products are
 * skipped.
 */
export const nearestProductIds = (
  points: number[][],
  spots: ProductSpot[],
  count: number,
): string[] => {
  if (points.length === 0) return [];
  const [acx, acy] = pointsCenter(points);
  const ids = spots
    .filter((s) => s.points.length > 0 && s.productIds.some(Boolean))
    .map((s) => {
      const [cx, cy] = pointsCenter(s.points);
      const dx = cx - acx;
      const dy = cy - acy;
      return { ids: s.productIds.filter(Boolean), d2: dx * dx + dy * dy };
    })
    .sort((a, z) => a.d2 - z.d2)
    .slice(0, count)
    .flatMap((s) => s.ids);
  return [...new Set(ids)];
};

/**
 * The spots an empty area's hover localizes against: the model's product
 * detections when they carry product ids, else the labeled/propagated areas
 * known for the same image. Pilot parcels come without recognized product ids,
 * so the second source is what lets their boxes point at the planogram.
 */
export const neighbourSpots = (
  detections: { points?: number[][] }[],
  detectionProductIds: (string | null | undefined)[],
  annotations: { points: number[][]; product_ids: string[] }[],
): ProductSpot[] => {
  const detectionSpots = detections
    .map((d, i) => ({
      points: d?.points ?? [],
      productIds: detectionProductIds[i]
        ? [detectionProductIds[i] as string]
        : [],
    }))
    .filter((s) => s.productIds.length > 0);
  if (detectionSpots.length > 0) return detectionSpots;
  return annotations
    .filter((a) => a.product_ids.length > 0)
    .map((a) => ({ points: a.points, productIds: a.product_ids }));
};
